import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import {
  Building2,
  FileText,
  Upload,
  Shield,
  Eye,
  Edit,
  Download,
  Link,
  X,
  MoreVertical,
  Search
} from 'lucide-react';
import { projectService } from '../../../src/services/project.service';
import { userService } from '../../../src/services/user.service';
import { documentService, Document } from '../../../src/services/document.service';
import { fileStorageService } from '../../../src/services/file-storage.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { useConfirmDialog } from '../../ui/confirm-dialog';

// Helper function to get status badge
export const getStatusBadge = (status: string) => {
  switch (status) {
    case "approved": return <Badge variant="default" className="bg-green-600">Approved</Badge>;
    case "under-review": return <Badge variant="secondary">Under Review</Badge>;
    case "rejected": return <Badge variant="destructive">Rejected</Badge>;
    case "draft": return <Badge variant="outline">Draft</Badge>;
    default: return <Badge variant="outline">{status}</Badge>;
  }
};

// Helper function to get file icon
export const getFileIcon = (fileType: string) => {
  switch (fileType.toLowerCase()) {
    case 'pdf': return '📄';
    case 'dwg': case 'dxf': return '📐';
    case 'doc': case 'docx': return '📝';
    case 'xls': case 'xlsx': return '📊';
    case 'ppt': case 'pptx': return '📋';
    case 'jpg': case 'png': case 'tiff': return '🖼️';
    default: return '📁';
  }
};

// Helper function to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Document Upload Modal Component
export function DocumentUploadModal({ onSuccess }: { onSuccess?: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [organizationId, setOrganizationId] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project: '',
    category: '',
    documentType: '',
    version: '1.0',
    tags: '',
    uploadedBy: 'Current User',
    isConfidential: false,
    expiryDate: '',
    notes: ''
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [dragActive, setDragActive] = useState(false);

  // Load projects when modal opens
  useEffect(() => {
    const loadProjects = async () => {
      if (!isOpen) return;

      try {
        // Get user profile to get organization ID
        const userProfile = await userService.getUserProfile();
        if (userProfile.success && userProfile.data?.organization_id) {
          setOrganizationId(userProfile.data.organization_id);

          // Load projects for the organization
          const projectsResponse = await projectService.getProjects(1, 100, {
            organization_id: userProfile.data.organization_id
          });

          if (projectsResponse.success && projectsResponse.data) {
            const projectsArray = Array.isArray(projectsResponse.data)
              ? projectsResponse.data
              : (projectsResponse.data as any).data;
            setProjects(projectsArray || []);
          }
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        showError({ title: 'Error', message: 'Failed to load projects' });
      }
    };

    loadProjects();
  }, [isOpen]);

  // Acceptable file types and sizes
  const acceptedFileTypes = {
    'Technical Drawings': ['.dwg', '.dxf', '.pdf', '.jpg', '.png', '.tiff'],
    'Regulatory Approvals': ['.pdf', '.doc', '.docx', '.jpg', '.png'],
    'Other Documents': ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.jpg', '.png', '.txt']
  };

  const maxFileSize = 50 * 1024 * 1024; // 50MB
  const maxFileSizeTechnical = 100 * 1024 * 1024; // 100MB for technical drawings

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Document title is required';
    }
    if (!formData.project) {
      newErrors.project = 'Project selection is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category selection is required';
    }
    if (!formData.documentType) {
      newErrors.documentType = 'Document type is required';
    }
    if (!selectedFile) {
      newErrors.file = 'Please select a file to upload';
    }

    // File validation
    if (selectedFile) {
      const fileExtension = '.' + selectedFile.name.split('.').pop()?.toLowerCase();
      const categoryTypes = acceptedFileTypes[formData.category as keyof typeof acceptedFileTypes];

      if (categoryTypes && !categoryTypes.includes(fileExtension)) {
        newErrors.file = `File type ${fileExtension} not allowed for ${formData.category}. Allowed types: ${categoryTypes.join(', ')}`;
      }

      const maxSize = formData.category === 'Technical Drawings' ? maxFileSizeTechnical : maxFileSize;
      if (selectedFile.size > maxSize) {
        newErrors.file = `File size exceeds ${maxSize / (1024 * 1024)}MB limit`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError({ title: 'Validation Error', message: 'Please fill in all required fields' });
      return;
    }

    if (!selectedFile) {
      showError({ title: 'Validation Error', message: 'Please select a file to upload' });
      return;
    }

    setIsLoading(true);

    try {
      // NEW TWO-STEP FLOW

      // Step 1: Upload file to storage
      const uploadResponse = await documentService.uploadFile(selectedFile, organizationId);

      if (!uploadResponse.success || !uploadResponse.data?.storage_id) {
        throw new Error('File upload failed');
      }

      const storage_id = uploadResponse.data.storage_id;

      // Step 2: Create document record with storage_id
      // Map UI category values to backend enum values
      const categoryMap: Record<string, string> = {
        'Technical Drawings': 'technical_drawings',
        'Regulatory Approvals': 'regulatory_approvals',
        'Audit Documentation': 'audit_documentation',
        'Project Documents': 'project_documents',
        'Other Documents': 'other'
      };

      const documentData = {
        organization_id: organizationId,
        project_id: formData.project,
        title: formData.title,
        document_number: `DOC-${Date.now()}`, // Generate document number
        category: (categoryMap[formData.category] || 'other') as any,
        document_type: formData.documentType as any,
        description: formData.description,
        storage_id: storage_id, // Link to uploaded file
        version: formData.version,
        confidentiality_level: formData.isConfidential ? 'confidential' : 'internal' as any,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        status: 'Approved' // Backend expects title case
      };

      const createResponse = await documentService.createDocument(documentData);

      if (!createResponse.success) {
        throw new Error('Failed to create document record');
      }

      showSuccess({ title: 'Success', message: 'Document uploaded successfully' });

      // Reset form and close modal
      setFormData({
        title: '',
        description: '',
        project: '',
        category: '',
        documentType: '',
        version: '1.0',
        tags: '',
        uploadedBy: 'Current User',
        isConfidential: false,
        expiryDate: '',
        notes: ''
      });
      setSelectedFile(null);
      setErrors({});
      setIsOpen(false);

      // Trigger refresh if callback provided
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Error uploading document:', error);
      showError({ title: 'Error', message: error.message || 'Failed to upload document' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <Upload className="h-4 w-4 mr-2" />
          Upload Document
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Document
          </DialogTitle>
          <DialogDescription>
            Upload project documents with proper categorization and metadata
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* File Upload Area */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">File Selection</h4>
            </div>

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-primary bg-primary/5'
                  : selectedFile
                    ? 'border-green-500 bg-green-50'
                    : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <div className="space-y-2">
                  <FileText className="mx-auto h-12 w-12 text-green-600" />
                  <div>
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedFile(null)}
                  >
                    Remove File
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Drop files here or click to upload</p>
                    <p className="text-sm text-muted-foreground">
                      Maximum file size: 50MB (100MB for technical drawings)
                    </p>
                  </div>
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                    accept=".pdf,.doc,.docx,.dwg,.dxf,.jpg,.png,.tiff,.xls,.xlsx,.ppt,.pptx,.txt"
                  />
                  <Button asChild variant="outline">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      Select File
                    </label>
                  </Button>
                </div>
              )}
            </div>
            {errors.file && <p className="text-sm text-destructive">{errors.file}</p>}
          </div>

          {/* Document Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Document Information</h4>
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-title">Document Title *</Label>
              <Input
                id="doc-title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Foundation Plan - Building A"
              />
              {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-description">Description</Label>
              <Textarea
                id="doc-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the document content..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="doc-version">Version</Label>
                <Input
                  id="doc-version"
                  value={formData.version}
                  onChange={(e) => setFormData({...formData, version: e.target.value})}
                  placeholder="1.0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="doc-tags">Tags (comma separated)</Label>
                <Input
                  id="doc-tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({...formData, tags: e.target.value})}
                  placeholder="foundation, structural, approved"
                />
              </div>
            </div>
          </div>

          {/* Project & Categorization */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Project & Categorization</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="doc-project">Project *</Label>
                <Select value={formData.project} onValueChange={(value) => setFormData({...formData, project: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.length > 0 ? (
                      projects.map((project: any) => (
                        <SelectItem key={project.project_id} value={project.project_id}>
                          {project.project_name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-projects" disabled>
                        No projects available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {errors.project && <p className="text-sm text-destructive">{errors.project}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="doc-category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Technical Drawings">Technical Drawings & Plans</SelectItem>
                    <SelectItem value="Regulatory Approvals">Regulatory Approvals</SelectItem>
                    <SelectItem value="Other Documents">Other Project Documents</SelectItem>
                  </SelectContent>
                </Select>
                {errors.category && <p className="text-sm text-destructive">{errors.category}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-type">Document Type *</Label>
              <Select value={formData.documentType} onValueChange={(value) => setFormData({...formData, documentType: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {formData.category === 'Technical Drawings' && (
                    <>
                      <SelectItem value="architectural_plan">Architectural Plan</SelectItem>
                      <SelectItem value="structural_drawing">Structural Drawing</SelectItem>
                      <SelectItem value="mep_drawing">MEP Drawing</SelectItem>
                      <SelectItem value="site_plan">Site Plan</SelectItem>
                      <SelectItem value="detail_drawing">Detail Drawing</SelectItem>
                      <SelectItem value="as_built">As-Built Drawing</SelectItem>
                    </>
                  )}
                  {formData.category === 'Regulatory Approvals' && (
                    <>
                      <SelectItem value="building_permit">Building Permit</SelectItem>
                      <SelectItem value="environmental_clearance">Environmental Clearance</SelectItem>
                      <SelectItem value="fire_safety">Fire Safety Approval</SelectItem>
                      <SelectItem value="occupancy_certificate">Occupancy Certificate</SelectItem>
                      <SelectItem value="compliance_certificate">Compliance Certificate</SelectItem>
                      <SelectItem value="inspection_report">Inspection Report</SelectItem>
                    </>
                  )}
                  {formData.category === 'Other Documents' && (
                    <>
                      <SelectItem value="specification">Specification</SelectItem>
                      <SelectItem value="contract">Contract Document</SelectItem>
                      <SelectItem value="report">Report</SelectItem>
                      <SelectItem value="correspondence">Correspondence</SelectItem>
                      <SelectItem value="invoice">Invoice</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              {errors.documentType && <p className="text-sm text-destructive">{errors.documentType}</p>}
            </div>
          </div>

          {/* Additional Settings */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Additional Settings</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expiry-date">Expiry Date (if applicable)</Label>
                <Input
                  id="expiry-date"
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <input
                  type="checkbox"
                  id="confidential"
                  checked={formData.isConfidential}
                  onChange={(e) => setFormData({...formData, isConfidential: e.target.checked})}
                  className="rounded border-border"
                />
                <Label htmlFor="confidential">Mark as Confidential</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-notes">Additional Notes</Label>
              <Textarea
                id="doc-notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional information or special instructions..."
                rows={2}
              />
            </div>
          </div>

          {/* File Type Information */}
          {formData.category && (
            <div className="p-4 bg-muted/50 rounded-lg">
              <h5 className="font-medium mb-2">Accepted File Types for {formData.category}:</h5>
              <p className="text-sm text-muted-foreground">
                {acceptedFileTypes[formData.category as keyof typeof acceptedFileTypes]?.join(', ')}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Maximum file size: {formData.category === 'Technical Drawings' ? '100MB' : '50MB'}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!selectedFile || isLoading}>
            {isLoading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload Document
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Document Actions Menu Component
export function DocumentActionsMenu({ document, onRefresh, showViewDownload = false }: { document: any; onRefresh?: () => void; showViewDownload?: boolean }) {
  const { showConfirm, ConfirmDialog } = useConfirmDialog();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleView = async () => {
    setIsViewing(true);
    try {
      const response = document.storage_id
        ? await fileStorageService.previewFile(document.storage_id)
        : await documentService.previewDocument(document.document_id);
      if (response.success && response.data) {
        const url = URL.createObjectURL(response.data);
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      showError({ title: 'Error', message: 'Failed to preview document' });
    } finally {
      setIsViewing(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = document.storage_id
        ? await fileStorageService.downloadFile(document.storage_id)
        : await documentService.downloadDocument(document.document_id);
      if (response.success && response.data) {
        const url = URL.createObjectURL(response.data);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = document.file_storage?.file_name || document.document_name || document.title || 'document';
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showSuccess({ title: 'Success', message: 'Document downloaded successfully' });
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      showError({ title: 'Error', message: 'Failed to download document' });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      const response = await documentService.shareDocument(document.document_id, 7);
      if (response.success && response.data?.share_url) {
        await navigator.clipboard.writeText(response.data.share_url);
        showSuccess({ title: 'Success', message: 'Share link copied to clipboard' });
      }
    } catch (error) {
      console.error('Error generating share link:', error);
      showError({ title: 'Error', message: 'Failed to generate share link' });
    }
  };

  const handleDelete = () => {
    const fileName = document.file_storage?.file_name || document.document_name || document.title || 'this document';

    showConfirm({
      title: 'Delete document',
      description: `Are you sure you want to delete "${fileName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          const response = await documentService.deleteDocument(document.document_id);
          if (response.success) {
            showSuccess({
              title: 'Document deleted',
              message: 'Document has been successfully deleted'
            });
            if (onRefresh) onRefresh();
          }
        } catch (error: any) {
          console.error('Error deleting document:', error);
          showError({
            title: 'Delete failed',
            message: error.message || 'Failed to delete document. Please try again.'
          });
        } finally {
          setIsDeleting(false);
        }
      }
    });
  };

  return (
    <>
      <ConfirmDialog />
      <div className="flex items-center gap-2">
        {showViewDownload && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={handleView}
            disabled={isViewing}
          >
            {isViewing ? (
              <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </Button>
        </>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" disabled={isDeleting}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {!showViewDownload && (
            <>
              <DropdownMenuItem onClick={handleView}>
                <Eye className="h-4 w-4 mr-2" />
                View Document
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem onClick={handleCopyLink}>
            <Link className="h-4 w-4 mr-2" />
            Copy Share Link
          </DropdownMenuItem>
          <DropdownMenuItem className="text-muted-foreground" disabled>
            <Edit className="h-4 w-4 mr-2" />
            Edit Details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDelete} className="text-destructive">
            <X className="h-4 w-4 mr-2" />
            {isDeleting ? 'Deleting...' : 'Delete'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </>
  );
}


export function Documents() {
  const [activeTab, setActiveTab] = useState("technical");
  const [selectedProject, setSelectedProject] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [documents, setDocuments] = useState<Document[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [organizationId, setOrganizationId] = useState('');

  // Load documents and projects
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Get user profile to get organization ID
      const userProfile = await userService.getUserProfile();
      if (userProfile.success && userProfile.data?.organization_id) {
        setOrganizationId(userProfile.data.organization_id);

        // Load projects
        const projectsResponse = await projectService.getProjects(1, 100, {
          organization_id: userProfile.data.organization_id
        });
        if (projectsResponse.success && projectsResponse.data) {
          const projectsArray = Array.isArray(projectsResponse.data)
            ? projectsResponse.data
            : (projectsResponse.data as any).data;
          setProjects(projectsArray || []);
        }

        const docsResponse = await documentService.getDocuments(1, 100, {
          organization_id: userProfile.data.organization_id
        });

        if (docsResponse.success && docsResponse.data) {
          const documentsArray = Array.isArray(docsResponse.data)
            ? docsResponse.data
            : (docsResponse.data as any).data ?? [];

          // Exclude photos uploaded from other pages (e.g. daily log attachments)
          setDocuments((documentsArray as Document[]).filter(d => d.document_type !== 'photo'));
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showError({ title: 'Error', message: 'Failed to load documents' });
    } finally {
      setIsLoading(false);
    }
  };

  // Sample documents data (fallback/demo)
  const sampleDocuments = [
    {
      id: 1,
      title: "Foundation Plan - Building A",
      description: "Detailed foundation plan showing reinforcement layout",
      project: "Residential Complex Alpha",
      projectId: "residential-alpha",
      category: "Technical Drawings",
      documentType: "architectural-plan",
      version: "2.1",
      fileSize: "15.2 MB",
      fileType: "PDF",
      uploadedBy: "John Doe",
      uploadDate: "2024-03-10",
      lastModified: "2024-03-12",
      tags: ["foundation", "structural", "approved"],
      isConfidential: false,
      expiryDate: "",
      downloadCount: 25,
      status: "approved"
    },
    {
      id: 2,
      title: "Structural Steel Details",
      description: "Steel connection details and specifications",
      project: "Hospital Wing Expansion",
      projectId: "hospital-expansion",
      category: "Technical Drawings",
      documentType: "structural-drawing",
      version: "1.3",
      fileSize: "22.8 MB",
      fileType: "DWG",
      uploadedBy: "Jane Smith",
      uploadDate: "2024-03-08",
      lastModified: "2024-03-11",
      tags: ["steel", "connections", "details"],
      isConfidential: false,
      expiryDate: "",
      downloadCount: 18,
      status: "under-review"
    },
    {
      id: 3,
      title: "Building Permit",
      description: "Municipal building permit for construction",
      project: "Residential Complex Alpha",
      projectId: "residential-alpha",
      category: "Regulatory Approvals",
      documentType: "building-permit",
      version: "1.0",
      fileSize: "2.1 MB",
      fileType: "PDF",
      uploadedBy: "Mike Johnson",
      uploadDate: "2024-02-15",
      lastModified: "2024-02-15",
      tags: ["permit", "regulatory", "approved"],
      isConfidential: false,
      expiryDate: "2025-02-15",
      downloadCount: 42,
      status: "approved"
    },
    {
      id: 4,
      title: "Fire Safety Clearance",
      description: "Fire department safety clearance certificate",
      project: "Hospital Wing Expansion",
      projectId: "hospital-expansion",
      category: "Regulatory Approvals",
      documentType: "fire-safety",
      version: "1.0",
      fileSize: "1.8 MB",
      fileType: "PDF",
      uploadedBy: "Sarah Wilson",
      uploadDate: "2024-03-05",
      lastModified: "2024-03-05",
      tags: ["fire-safety", "clearance", "approved"],
      isConfidential: false,
      expiryDate: "2025-03-05",
      downloadCount: 15,
      status: "approved"
    },
    {
      id: 5,
      title: "Material Specifications",
      description: "Detailed specifications for concrete and steel materials",
      project: "Industrial Warehouse",
      projectId: "industrial-warehouse",
      category: "Other Documents",
      documentType: "specification",
      version: "1.2",
      fileSize: "5.4 MB",
      fileType: "PDF",
      uploadedBy: "Alex Brown",
      uploadDate: "2024-03-01",
      lastModified: "2024-03-09",
      tags: ["specifications", "materials", "concrete", "steel"],
      isConfidential: false,
      expiryDate: "",
      downloadCount: 31,
      status: "approved"
    },
    {
      id: 6,
      title: "Project Contract",
      description: "Main construction contract agreement",
      project: "Residential Complex Alpha",
      projectId: "residential-alpha",
      category: "Other Documents",
      documentType: "contract",
      version: "1.0",
      fileSize: "8.7 MB",
      fileType: "PDF",
      uploadedBy: "Tom Wilson",
      uploadDate: "2024-01-20",
      lastModified: "2024-01-20",
      tags: ["contract", "agreement", "confidential"],
      isConfidential: true,
      expiryDate: "",
      downloadCount: 8,
      status: "approved"
    }
  ];

  // Create project lookup map
  const projectMap = new Map(projects.map(p => [p.project_id, p.project_name]));

  const filteredDocuments = documents.filter(doc => {
    const matchesProject = selectedProject === "all" || doc.project_id === selectedProject;
    const docName = doc.document_name || (doc as any).title || '';
    const docDesc = doc.description || '';
    const docTags = doc.tags || [];
    const matchesSearch = docName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         docDesc.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         docTags.some((tag: string) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    // Map document_category to tab categories (backend returns lowercase with underscores)
    const category = doc.document_category || (doc as any).category || '';
    const matchesTab =
      (activeTab === "technical" && category === "technical_drawings") ||
      (activeTab === "regulatory" && category === "regulatory_approvals") ||
      (activeTab === "other" && category !== "technical_drawings" && category !== "regulatory_approvals");

    return matchesProject && matchesSearch && matchesTab;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved": return <Badge variant="default" className="bg-green-600">Approved</Badge>;
      case "under-review": return <Badge variant="secondary">Under Review</Badge>;
      case "rejected": return <Badge variant="destructive">Rejected</Badge>;
      case "draft": return <Badge variant="outline">Draft</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf': return '📄';
      case 'dwg': case 'dxf': return '📐';
      case 'doc': case 'docx': return '📝';
      case 'xls': case 'xlsx': return '📊';
      case 'ppt': case 'pptx': return '📋';
      case 'jpg': case 'png': case 'tiff': return '🖼️';
      default: return '📁';
    }
  };

  const getTabCount = (category: string) => {
    return documents.filter(doc => {
      const matchesProject = selectedProject === "all" || doc.project_id === selectedProject;
      const docCategory = doc.document_category || (doc as any).category || '';
      return matchesProject &&
        ((category === "technical" && docCategory === "technical_drawings") ||
         (category === "regulatory" && docCategory === "regulatory_approvals") ||
         (category === "other" && docCategory !== "technical_drawings" && docCategory !== "regulatory_approvals"));
    }).length;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Documents</h2>
          <p className="text-muted-foreground">Project documents, drawings, and specifications</p>
        </div>
        <DocumentUploadModal onSuccess={loadData} />
      </div>


      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" />
              Total Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{documents.length}</div>
            <p className="text-xs text-blue-600 mt-1">Across all projects</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4 text-purple-600" />
              Technical Drawings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{documents.filter(d => d.document_category === 'technical_drawings').length}</div>
            <p className="text-xs text-purple-600 mt-1">Plans & drawings</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-600" />
              Approvals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{documents.filter(d => d.document_category === 'regulatory_approvals').length}</div>
            <p className="text-xs text-green-600 mt-1">Regulatory documents</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Eye className="h-4 w-4 text-orange-600" />
              Pending Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-700">{documents.filter(d => d.status === 'under-review').length}</div>
            <p className="text-xs text-orange-600 mt-1">Awaiting approval</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Document Library</CardTitle>
          <CardDescription>Manage and organize project documents</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search documents, descriptions, or tags..." 
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      {project.project_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === "list" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                List
              </Button>
              <Button
                variant={viewMode === "grid" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("grid")}
              >
                Grid
              </Button>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="technical" className="flex items-center gap-2">
                📐 Technical Drawings & Plans 
                <Badge variant="secondary" className="ml-1">{getTabCount("technical")}</Badge>
              </TabsTrigger>
              <TabsTrigger value="regulatory" className="flex items-center gap-2">
                🏛️ Regulatory Approvals
                <Badge variant="secondary" className="ml-1">{getTabCount("regulatory")}</Badge>
              </TabsTrigger>
              <TabsTrigger value="other" className="flex items-center gap-2">
                📁 Other Documents
                <Badge variant="secondary" className="ml-1">{getTabCount("other")}</Badge>
              </TabsTrigger>
            </TabsList>
            
            {["technical", "regulatory", "other"].map((tab) => (
              <TabsContent key={tab} value={tab} className="space-y-4">
                {viewMode === "list" ? (
                  <div className="space-y-3">
                    {filteredDocuments.map((doc) => {
                      // NEW: Use file_storage fields first, fallback to legacy
                      const fileName = doc.file_storage?.file_name || doc.document_name || (doc as any).title || 'Untitled';
                      const fileSize = doc.file_storage?.file_size_mb
                        ? `${doc.file_storage.file_size_mb.toFixed(2)} MB`
                        : doc.file_size_bytes
                        ? `${(doc.file_size_bytes / 1024 / 1024).toFixed(2)} MB`
                        : (doc as any).fileSize || 'N/A';
                      const mimeType = doc.file_storage?.mime_type || doc.mime_type || '';
                      const fileType = mimeType.split('/')[1]?.toUpperCase() || 'FILE';
                      const uploadDate = doc.file_storage?.uploaded_at || doc.created_at;
                      const formattedDate = uploadDate ? new Date(uploadDate).toLocaleDateString() : 'N/A';
                      const projectName = projectMap.get(doc.project_id) || doc.project_id;

                      return (
                        <div key={doc.document_id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <span className="text-2xl">{getFileIcon(fileType)}</span>
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-medium">{fileName}</h4>
                                  {doc.status && getStatusBadge(doc.status)}
                                  <Badge variant="outline">v{doc.version}</Badge>
                                  {doc.confidentiality_level === 'confidential' && <Badge variant="destructive">Confidential</Badge>}
                                </div>
                                <p className="text-sm text-muted-foreground mb-3">{doc.description || 'No description'}</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div>
                                    <span className="font-medium">Project:</span>
                                    <p className="text-muted-foreground">{projectName}</p>
                                  </div>
                                  <div>
                                    <span className="font-medium">Type:</span>
                                    <p className="text-muted-foreground">{doc.document_type?.replace('_', ' ')}</p>
                                  </div>
                                  <div>
                                    <span className="font-medium">Uploaded:</span>
                                    <p className="text-muted-foreground">{formattedDate}</p>
                                  </div>
                                  <div>
                                    <span className="font-medium">Size:</span>
                                    <p className="text-muted-foreground">{fileSize}</p>
                                  </div>
                                </div>
                                {doc.tags && doc.tags.length > 0 && (
                                  <div className="flex items-center gap-2 mt-3">
                                    <span className="text-sm font-medium">Tags:</span>
                                    {doc.tags.map((tag: string) => (
                                      <Badge key={tag} variant="outline" className="text-xs">
                                        {tag}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <DocumentActionsMenu document={doc} onRefresh={loadData} showViewDownload={true} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredDocuments.map((doc) => {
                      // NEW: Use file_storage fields first, fallback to legacy
                      const fileName = doc.file_storage?.file_name || doc.document_name || (doc as any).title || 'Untitled';
                      const fileSize = doc.file_storage?.file_size_mb
                        ? `${doc.file_storage.file_size_mb.toFixed(2)} MB`
                        : doc.file_size_bytes
                        ? `${(doc.file_size_bytes / 1024 / 1024).toFixed(2)} MB`
                        : 'N/A';
                      const mimeType = doc.file_storage?.mime_type || doc.mime_type || '';
                      const fileType = mimeType.split('/')[1]?.toUpperCase() || 'FILE';
                      const uploadDate = doc.file_storage?.uploaded_at || doc.created_at;
                      const formattedDate = uploadDate ? new Date(uploadDate).toLocaleDateString() : 'N/A';
                      const projectName = projectMap.get(doc.project_id) || doc.project_id;

                      return (
                        <Card key={doc.document_id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <span className="text-3xl">{getFileIcon(fileType)}</span>
                              <DocumentActionsMenu document={doc} onRefresh={loadData} showViewDownload={false} />
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                {doc.status && getStatusBadge(doc.status)}
                                <Badge variant="outline">v{doc.version}</Badge>
                              </div>
                              <h4 className="font-medium line-clamp-2">{fileName}</h4>
                              <p className="text-sm text-muted-foreground line-clamp-2">{doc.description || 'No description'}</p>
                              <div className="text-xs text-muted-foreground">
                                <p>{projectName}</p>
                                <p>{fileSize} • {formattedDate}</p>
                              </div>
                              {doc.tags && doc.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {doc.tags.slice(0, 3).map((tag: string) => (
                                    <Badge key={tag} variant="outline" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                  {doc.tags.length > 3 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{doc.tags.length - 3}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
                
                {filteredDocuments.length === 0 && (
                  <div className="text-center py-12">
                    <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No documents found</h3>
                    <p className="text-muted-foreground">
                      {searchQuery || selectedProject !== "all" 
                        ? "Try adjusting your search criteria or filters"
                        : "Upload your first document to get started"
                      }
                    </p>
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
